//librerías
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Container, Modal, Form, Row, Col, ButtonGroup } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faRedo, faTrashCan, faFileExcel, faCalendarAlt, faTable } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as XLSX from 'xlsx';

//context
import { AuthContext } from "../../../context/AuthContext";

//interfaces
import { ActividadIndex } from "../../../interfaces/ActividadIndexInterface";
//types
import { ServicioProgramado } from "../../../types/ServiciosProgramados";

//custom hooks
import useFetch from "../../../hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import CustomTable from "../../Formularios/CustomTable/CustomTable";
import useDeleteActividad from "../../../hooks/useDeleteActividad";
import ServiciosCalendar from "../ServiciosCalendar/ServiciosCalendar";

//estilos
const classes = require("./ServiciosTable.module.css").default;

/**
 * ServiciosTable Component
 * @description: Componente que muestra el paginado de los servicios.
 * @date 14/11/2022.
 * @param Props Recibe los servicios en un arreglo, la función changeEtapa que actualiza la
 * etapa del contenido Home, la función changeServicio que actualiza el id del servicio al que se quiere saber su detalle.
 * @returns JSX de la tabla del servicio.
 */

const ServiciosProgramados = () => {
  const authContext = useContext(AuthContext);
  const [actividades, setActividades] = useState<ActividadIndex[]>();
  const { sendRequest } = useFetch();
  const navigate = useNavigate();
  const { eliminarServicio } = useDeleteActividad(setActividades);
  
  const [vistaCalendario, setVistaCalendario] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Función para abrir el modal
  const handleOpenModal = () => {
  setStartDate(prevStart => prevStart || '');
  setEndDate(prevEnd => prevEnd || '');
  setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  const exportToExcel = () => {
    if (!actividades || actividades.length === 0) {
      alert("No hay servicios programados para exportar");
      return;
    }
    
    let startDateTime: Date | null = null;
    let endDateTime: Date | null = null;
    
    if (startDate) {
      const [year, month, day] = startDate.split('-');
      startDateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
    }
    
    if (endDate) {
      const [year, month, day] = endDate.split('-');
      endDateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 23, 59, 59, 999);
    }
    
    let filteredActividades = [...actividades];
    
    if (startDateTime || endDateTime) {
      filteredActividades = actividades.filter(servicio => {

        if (!servicio.fecha) return false;
        
        const servicioDate = new Date(servicio.fecha);

        const isAfterStartDate = !startDateTime || servicioDate >= startDateTime;
        const isBeforeEndDate = !endDateTime || servicioDate <= endDateTime;
        
        return isAfterStartDate && isBeforeEndDate;
      });
    }
    
    if (filteredActividades.length === 0) {
      alert("No hay servicios programados en el rango de fechas seleccionado");
      return;
    }

    const dataToExport = filteredActividades.map(servicio => {

      const elementoCaptura = servicio.etapa.find(
        (element) => element.tipo_etapa_id === 1
      );

      const personaCaptura = elementoCaptura
        ? `${elementoCaptura.usuario.nombre} ${elementoCaptura.usuario.apellido_paterno}`
        : "";

      const sucursalNombre = servicio.servicio.sucursal?.nombre || "";
      const sucursalId = servicio.servicio.sucursal?.id || "";
      const sucursalIdNMP = servicio.servicio.sucursal?.id_nmp || "";
      
      return {
        "Número de ticket": servicio.servicio.folio || "",
        "Nombre de sucursal": sucursalNombre,
        "Número de sucursal": sucursalId,
        "Número de sucursal NMP": sucursalIdNMP,
        "Fecha y hora de la cita": servicio.fecha || "",
        "Técnico": `${servicio.usuario.nombre} ${servicio.usuario.apellido_paterno} ${servicio.usuario.apellido_materno}`,
        "Tipo de servicio": servicio.tipo_servicio.nombre,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Servicios Programados");
    
    let fileName = "Servicios_Programados";
    if (startDate && endDate) {
      fileName += `_${startDate}_al_${endDate}`;
    } else if (startDate) {
      fileName += `_desde_${startDate}`;
    } else if (endDate) {
      fileName += `_hasta_${endDate}`;
    } else {
      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      fileName += `_${formattedDate}`;
    }
    
    XLSX.writeFile(workbook, `${fileName}.xlsx`);

    handleCloseModal();
  };

  const transformData = useCallback((variable: any) => {
    setActividades(variable.data);
  }, []);

  useEffect(() => {
    const plataformaActiva = authContext.plataformas.length > 0 ? authContext.plataformas[0].id : null;
    const url = plataformaActiva 
      ? `/actividad?includeAll=true&ultima_etapa[eq]=2&plataforma_activa=${plataformaActiva}`
      : "/actividad?includeAll=true&ultima_etapa[eq]=2";
    
    sendRequest(
      {
        url: url,
      },
      transformData
    );
  }, [sendRequest, transformData, authContext.plataformas]);

  const columns = useMemo<ColumnDef<ServicioProgramado>[]>(
    () => [
      {
        header: "Órden de servicio",
        accessorKey: "id",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Fecha y hora de la cita",
        accessorKey: "fecha",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Persona que capura",
        accessorFn: (row) => {
          let elemento = row.etapa.find(
            (element) => element.tipo_etapa_id === 1
          );
          if (elemento)
            return `${elemento?.usuario.nombre} ${elemento?.usuario.apellido_paterno}`;
          else return "";
        },
        id: "persona_captura",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Sucursal",
        accessorFn: (row) =>
          row.servicio.sucursal.nombre ? row.servicio.sucursal.nombre : "",
        id: "Sucursal",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Folio",
        accessorFn: (row) => (row.servicio.folio ? row.servicio.folio : ""),
        id: "Folio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Técnico",
        accessorFn: (row) =>
          `${row.usuario.nombre} ${row.usuario.apellido_paterno} ${row.usuario.apellido_materno}`,
        id: "tecnico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Tipo de servicio",
        accessorFn: (row) => row.tipo_servicio.nombre,
        id: "tipo_servicio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "VIN",
        accessorFn: (row) => (row.unidad.vin ? row.unidad.vin : ""),
        id: "vin",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Placas",
        accessorFn: (row) => (row.unidad.placa ? row.unidad.placa : ""),
        id: "placa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            <Button
              title="Editar servicio"
              style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
              onClick={() =>
                navigate(`/main/servicios/edit/${info.row.original.id}`)
              }
              disabled={!authContext.tokenCan("Servicio:update")}
            >
              <FontAwesomeIcon icon={faEdit as IconProp} />
            </Button>
            <Button
              title="Reprogramar servicio"
              style={{ backgroundColor: "#2E86C1", borderColor: "white" }}
              onClick={() =>
                navigate(`/main/servicios/programar/${info.row.original.id}`)
              }
              disabled={!authContext.tokenCan("Servicio:update")}
            >
              <FontAwesomeIcon icon={faRedo as IconProp} />
            </Button>
            {authContext.tokenCan("Actividad:delete") && (
              <Button
                title="Eliminar servicio"
                style={{ backgroundColor: "#21618C", borderColor: "white" }}
                onClick={() => eliminarServicio(info.row.original.id)}
              >
                <FontAwesomeIcon icon={faTrashCan as IconProp} />
              </Button>
            )}
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [navigate, authContext, eliminarServicio]
  );

  if (!actividades) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container fluid style={{ paddingBottom: "1rem", paddingLeft: "2rem", paddingRight: "2rem" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1>Servicios Programados</h1>
        <div className="d-flex">
          <ButtonGroup className="me-2" style={{ boxShadow: '0 2px 5px rgba(0,0,0,0.1)', borderRadius: '6px' }}>
            <Button
              variant={!vistaCalendario ? "primary" : "outline-primary"}
              onClick={() => setVistaCalendario(false)}
              title="Vista de tabla"
              style={{ 
                borderRadius: vistaCalendario ? '6px 0 0 6px' : '6px',
                fontWeight: !vistaCalendario ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faTable as IconProp} /> Tabla
            </Button>
            <Button
              variant={vistaCalendario ? "primary" : "outline-primary"}
              onClick={() => setVistaCalendario(true)}
              title="Vista de calendario"
              style={{ 
                borderRadius: !vistaCalendario ? '0 6px 6px 0' : '6px',
                fontWeight: vistaCalendario ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt as IconProp} /> Calendario
            </Button>
          </ButtonGroup>
          <Button 
            variant="success" 
            onClick={handleOpenModal}
            title="Exportar a Excel"
            style={{ 
              backgroundColor: "#28a745", 
              borderColor: "white",
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}
          >
            <FontAwesomeIcon icon={faFileExcel as IconProp} /> Exportar a Excel
          </Button>
        </div>
      </div>
      
      <div style={{ 
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1
      }}>
        {vistaCalendario ? (

          actividades && <ServiciosCalendar actividades={actividades} />
        ) : (

          <CustomTable
            {...{
              data: actividades,
              columns,
            }}
          />
        )}
      </div>
      
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #eaeaea' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileExcel as IconProp} style={{ marginRight: '10px', color: '#28a745' }} />
            Exportar Servicios Programados
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Seleccione el rango de fechas para exportar los servicios programados:</p>
          <Form>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Inicial</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Final</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #eaeaea' }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseModal}
            style={{ borderRadius: '6px' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={exportToExcel}
            style={{ 
              backgroundColor: "#28a745", 
              borderColor: "#28a745",
              borderRadius: '6px'
            }}
          >
            <FontAwesomeIcon icon={faFileExcel as IconProp} style={{ marginRight: '5px' }} />
            Exportar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ServiciosProgramados;